export type ListingHealthInput = {
    providerKind: "breeder" | "shelter_rescue" | null;
    sexLabel?: string;
    providerName?: string;
    healthTests: string[];
    completedCare: string[];
    reportedHealth: string[];
    included: string[];
    description: string;
};

export type CareKey =
    | "special"
    | "spay"
    | "vaccines"
    | "deworming"
    | "microchip"
    | "vet";

export type CareStatus = "recorded" | "promised" | "not_listed" | "special";

export type CareChip = {
    key: CareKey;
    status: CareStatus;
    label: string;
};

export type ListingHealth = {
    tone: "recorded" | "promised";
    care: CareChip[];
    evaluations: string[];
    note: string | null;
};

type StandardCareKey = Exclude<CareKey, "special">;
type StandardCareStatus = Exclude<CareStatus, "special">;

const CARE_KEYS: readonly StandardCareKey[] = [
    "spay",
    "vaccines",
    "deworming",
    "microchip",
    "vet",
];

const GAP_KEYS: readonly StandardCareKey[] = ["spay", "vaccines", "microchip"];

const CARE_PATTERNS: Record<StandardCareKey, RegExp> = {
    spay: /\bspay|\bneuter/i,
    vaccines: /vaccin|\bshots?\b|\bdhpp\b|\bdapp\b|\brabies\b|\bbordetella\b/i,
    deworming: /de-?worm/i,
    microchip: /micro-?chip/i,
    vet: /vet(?:erinary)?\s+(?:health\s+)?(?:check|checks|checkup|exam|exams|visit)|wellness exam|health certificate|bill of health|vet checked/i,
};

const FUTURE_MARKER =
    /\bwill\b|\bbefore\s+(?:going|they go|he goes|she goes|coming|heading)\s+(?:home|to)\b|\bbefore pickup\b|\bbefore\s+(?:his|her|their)\s+new home\b|\bprior to\b|\bat pickup\b|\bupon pickup\b|\bdue\b|\bmust be\b|\bto be\s+(?:spayed|neutered|vaccinated|dewormed|microchipped)\b|\bscheduled\b|\bonce\s+(?:he|she|they)\b/i;

const COMPLETED_MARKER =
    /\bhas\s+(?:been|received|had)\b|\bhave\s+(?:been|received|had)\b|\bis\s+(?:spayed|neutered|vaccinated|dewormed|microchipped|up to date|current)\b|\balready\b|\bup[- ]to[- ]date\b|\bcurrent on\b|\breceived\b|\bperformed\b|\bcompleted\b|\bchecked\b|\bexamined\b|\bcertified\b|\b(?:spayed|neutered|vaccinated|dewormed|microchipped)\b/i;

const POOL_ONLY_COMPLETED_MARKER = /\bprovided\b|\bhealthy for travel\b/i;

const EVALUATION_EXCLUSIONS = [
    ...Object.values(CARE_PATTERNS),
    /heart\s*worm/i,
    /fecal/i,
    /travel/i,
    /pupp/i,
    /behavio/i,
    /blood/i,
    /snap|4dx|idexx/i,
];

const INSURANCE_PATTERN =
    /trupanion|pet insurance|insurance (?:plan|coverage|policy)/i;
const RECORDS_PATTERN =
    /vet records|health records|health documentation|vaccination records|medical records/i;

function normalizeList(items: string[]): string[] {
    return items.map((item) => item.trim()).filter(Boolean);
}

function getOngoingHealthSection(description: string): string {
    const lines = description.split(/\r?\n/);
    const start = lines.findIndex((line) =>
        /^#{1,6}\s*ongoing health/im.test(line.trim()),
    );
    if (start === -1) return "";

    const end = lines.findIndex(
        (line, index) => index > start && /^#/.test(line.trim()),
    );
    return lines.slice(start + 1, end === -1 ? undefined : end).join("\n");
}

function splitProseSentences(description: string): string[] {
    return getOngoingHealthSection(description)
        .replace(/(^|\n)\s*[-*]\s+/g, "$1")
        .split(/[.;!?\n]+/)
        .map((sentence) => sentence.trim())
        .filter(Boolean);
}

function markerPositions(pattern: RegExp, sentence: string): number[] {
    return [...sentence.matchAll(new RegExp(pattern.source, "gi"))].flatMap(
        (match) => (match.index === undefined ? [] : [match.index]),
    );
}

function classifyOccurrence(
    sentence: string,
    keywordStart: number,
    keywordEnd: number,
): "recorded" | "promised" | "unknown" {
    const futurePositions = markerPositions(FUTURE_MARKER, sentence);
    const completedPositions = markerPositions(COMPLETED_MARKER, sentence);

    if (futurePositions.some((position) => position < keywordStart)) {
        return "promised";
    }
    if (completedPositions.some((position) => position < keywordStart)) {
        return "recorded";
    }
    if (futurePositions.some((position) => position >= keywordEnd)) {
        return "promised";
    }
    if (completedPositions.length > 0) {
        return "recorded";
    }
    return "unknown";
}

function proseStatus(
    key: StandardCareKey,
    sentences: string[],
): "recorded" | "promised" | "unknown" {
    const statuses = sentences.flatMap((sentence) => {
        const pattern = CARE_PATTERNS[key];
        return [...sentence.matchAll(new RegExp(pattern.source, "gi"))].map(
            (match) =>
                classifyOccurrence(
                    sentence,
                    match.index ?? 0,
                    (match.index ?? 0) + match[0].length,
                ),
        );
    });

    if (statuses.includes("promised")) return "promised";
    if (statuses.includes("recorded")) return "recorded";
    return "unknown";
}

function completedCareCarriesKey(
    key: StandardCareKey,
    completedCare: string[],
): boolean {
    return completedCare.some((item) => CARE_PATTERNS[key].test(item));
}

function poolStatus(
    key: StandardCareKey,
    pool: string[],
    providerKind: ListingHealthInput["providerKind"],
    completedCare: string[],
): "recorded" | "promised" | "unknown" {
    const items = pool.filter((item) => CARE_PATTERNS[key].test(item));
    if (items.length === 0) return "unknown";

    const recorded = items.some(
        (item) =>
            COMPLETED_MARKER.test(item) ||
            POOL_ONLY_COMPLETED_MARKER.test(item),
    );
    const promised = items.some((item) => FUTURE_MARKER.test(item));
    if (recorded && promised) return "promised";
    if (recorded) return "recorded";
    if (promised) return "promised";
    if (
        providerKind === "shelter_rescue" &&
        completedCareCarriesKey(key, completedCare)
    ) {
        return "recorded";
    }
    return "promised";
}

function resolveStatus(
    key: StandardCareKey,
    sentences: string[],
    pool: string[],
    providerKind: ListingHealthInput["providerKind"],
    completedCare: string[],
): { status: StandardCareStatus; hasEvidence: boolean } {
    const fromProse = proseStatus(key, sentences);
    if (fromProse !== "unknown") {
        return { status: fromProse, hasEvidence: true };
    }

    const fromPool = poolStatus(key, pool, providerKind, completedCare);
    if (fromPool !== "unknown") {
        return { status: fromPool, hasEvidence: true };
    }

    if (completedCareCarriesKey(key, completedCare)) {
        return { status: "recorded", hasEvidence: false };
    }

    return { status: "not_listed", hasEvidence: false };
}

function getCareLabel(
    key: StandardCareKey,
    status: StandardCareStatus,
    sexLabel: string,
): string {
    if (key === "spay") {
        if (status === "recorded") {
            if (sexLabel.toLowerCase() === "female") return "Spayed";
            if (sexLabel.toLowerCase() === "male") return "Neutered";
            return "Spayed or neutered";
        }
        if (status === "promised") return "Spay/neuter before pickup";
        return "Spay/neuter not listed";
    }
    if (key === "vaccines") {
        if (status === "recorded") return "Vaccinated";
        if (status === "promised") return "Vaccines before home";
        return "Vaccines not listed";
    }
    if (key === "deworming") {
        if (status === "recorded") return "Dewormed";
        return "Deworming before home";
    }
    if (key === "microchip") {
        if (status === "recorded") return "Microchipped";
        if (status === "promised") return "Microchip before home";
        return "Microchip not listed";
    }
    if (status === "recorded") return "Vet bill of health";
    if (status === "promised") return "Vet check before home";
    return "Vet bill of health";
}

function evaluationName(raw: string): string {
    const separator = raw.indexOf(" — ");
    return (separator === -1 ? raw : raw.slice(0, separator)).trim();
}

function shortEvaluationLabel(name: string): string {
    const groups = [...name.matchAll(/\(([^)]*)\)/g)].map(
        (match) => match[1] ?? "",
    );
    const codes = groups
        .flatMap((group) => group.split(/[\s,]+/))
        .filter((token) => /^[A-Z][A-Z0-9]*(?:-[A-Za-z0-9]+)?$/.test(token));
    const cleaned = name
        .replace(/\([^)]*\)/g, " ")
        .replace(/\s*[-–]?\s*DNA Test\b/gi, " ")
        .replace(/\s+/g, " ")
        .trim()
        .replace(/[-–]\s*$/, "")
        .trim();
    const lastCode = codes.at(-1);
    const label = lastCode && cleaned.length > 24 ? lastCode : cleaned;
    return label ? label[0].toUpperCase() + label.slice(1) : "";
}

function getEvaluations(healthTests: string[]): string[] {
    const labels: string[] = [];
    const seen = new Set<string>();

    for (const raw of healthTests) {
        const name = evaluationName(raw);
        if (
            !name ||
            EVALUATION_EXCLUSIONS.some((pattern) => pattern.test(name))
        ) {
            continue;
        }

        const lowerName = name.toLowerCase();
        const label = lowerName.includes("hip")
            ? "Hips"
            : lowerName.includes("elbow")
              ? "Elbows"
              : /cardiac|heart/i.test(name)
                ? "Cardiac"
                : /ophthalm|\beyes?\b/i.test(name)
                  ? "Eyes"
                  : lowerName.includes("patella")
                    ? "Patella"
                    : shortEvaluationLabel(name);

        if (label && !seen.has(label)) {
            seen.add(label);
            labels.push(label);
        }
    }

    return labels;
}

function hasPositiveMatch(
    texts: string[],
    pattern: RegExp,
    isPositive: (text: string) => boolean = () => true,
): boolean {
    return texts.some((text) => pattern.test(text) && isPositive(text));
}

function hasGuarantee(text: string): boolean {
    for (const match of text.matchAll(/guarantee/gi)) {
        const start = match.index ?? 0;
        const preceding = text.slice(Math.max(0, start - 12), start);
        if (!/\bno\s+/i.test(preceding)) return true;
    }
    return false;
}

function asSentence(item: string): string {
    const sentence = item[0].toUpperCase() + item.slice(1);
    return /[.!?]$/.test(sentence) ? sentence : `${sentence}.`;
}

function getNote(
    reportedHealth: string[],
    providerKind: ListingHealthInput["providerKind"],
    providerName: string,
    included: string[],
    proseSentences: string[],
): string | null {
    const sentences: string[] = [];
    if (reportedHealth.length > 0) {
        sentences.push(
            `Reported in the listing: ${reportedHealth.join(", ")}.`,
        );
    } else {
        const evidence = [...proseSentences, ...included];
        const insuranceItem = included.find(
            (item) => item.length <= 60 && INSURANCE_PATTERN.test(item),
        );
        if (hasPositiveMatch(evidence, INSURANCE_PATTERN)) {
            sentences.push(
                insuranceItem
                    ? asSentence(insuranceItem)
                    : /trupanion/i.test(evidence.join(" "))
                      ? "Trupanion insurance mentioned in the listing."
                      : "Pet insurance mentioned in the listing.",
            );
        }

        const guaranteeItem = included.find(
            (item) => item.length <= 60 && hasGuarantee(item),
        );
        if (hasPositiveMatch(evidence, /guarantee/i, hasGuarantee)) {
            sentences.push(
                guaranteeItem
                    ? asSentence(guaranteeItem)
                    : "Comes with a health guarantee.",
            );
        }

        if (hasPositiveMatch(evidence, RECORDS_PATTERN)) {
            sentences.push("Vet records included.");
        }
    }

    if (
        sentences.length < 2 &&
        providerKind === "shelter_rescue" &&
        providerName
    ) {
        sentences.push(`Reported by ${providerName}.`);
    }

    return sentences.length > 0 ? sentences.slice(0, 2).join(" ") : null;
}

function getCareChips(
    reportedHealth: string[],
    resolved: Record<
        StandardCareKey,
        { status: StandardCareStatus; hasEvidence: boolean }
    >,
    providerKind: ListingHealthInput["providerKind"],
    sexLabel: string,
): CareChip[] {
    const care: CareChip[] = [];
    if (reportedHealth.length > 0) {
        const label =
            reportedHealth.length === 1 && reportedHealth[0].length <= 22
                ? reportedHealth[0][0].toUpperCase() +
                  reportedHealth[0].slice(1)
                : reportedHealth.length === 1
                  ? "Medical note on file"
                  : `${reportedHealth.length} medical notes on file`;
        care.push({ key: "special", status: "special", label });
    }

    for (const key of CARE_KEYS) {
        const { status, hasEvidence } = resolved[key];
        if (key === "spay" && providerKind === "breeder" && !hasEvidence) {
            continue;
        }
        if (status === "recorded" || status === "promised") {
            care.push({
                key,
                status,
                label: getCareLabel(key, status, sexLabel),
            });
        }
        if (care.length === 4) break;
    }

    if (providerKind === "shelter_rescue" && care.length < 4) {
        let gapCount = 0;
        for (const key of GAP_KEYS) {
            if (resolved[key].status !== "not_listed") continue;
            care.push({
                key,
                status: "not_listed",
                label: getCareLabel(key, "not_listed", sexLabel),
            });
            gapCount += 1;
            if (care.length === 4 || gapCount === 2) break;
        }
    }

    return care;
}

export function deriveListingHealth(
    input: ListingHealthInput,
): ListingHealth | null {
    const healthTests = normalizeList(input.healthTests);
    const completedCare = normalizeList(input.completedCare);
    const reportedHealth = normalizeList(input.reportedHealth);
    const included = normalizeList(input.included);
    const sexLabel = input.sexLabel?.trim() ?? "";
    const providerName = input.providerName?.trim() ?? "";
    const proseSentences = splitProseSentences(input.description.trim());
    const pool = [...healthTests, ...included];
    const resolved = Object.fromEntries(
        CARE_KEYS.map((key) => [
            key,
            resolveStatus(
                key,
                proseSentences,
                pool,
                input.providerKind,
                completedCare,
            ),
        ]),
    ) as Record<
        StandardCareKey,
        { status: StandardCareStatus; hasEvidence: boolean }
    >;
    const care = getCareChips(
        reportedHealth,
        resolved,
        input.providerKind,
        sexLabel,
    );

    const evaluations = getEvaluations(healthTests);
    const note = getNote(
        reportedHealth,
        input.providerKind,
        providerName,
        included,
        proseSentences,
    );

    if (care.length === 0 && evaluations.length === 0) return null;

    return {
        tone: care.some((chip) => chip.status === "promised")
            ? "promised"
            : "recorded",
        care,
        evaluations,
        note,
    };
}
